import { AwsRum, AwsRumConfig } from 'aws-rum-web';
import awsExports from './aws-exports';

let awsRum: AwsRum | undefined;

const RUM_CONSENT_KEY = 'pearsana.rum.cookieConsent';

export type RumConsent = 'accepted' | 'rejected' | 'unset';

/**
 * Suppress sendBeacon presigned-URL 404.
 *
 * The RUM data plane returns 404 for query-string SigV4 (presigned) requests
 * sent via navigator.sendBeacon during page-hide. Patching at the prototype
 * level ensures the override applies regardless of when BeaconHttpHandler
 * resolves the reference.
 *
 * Regular dispatches use fetch (header-based SigV4) on a 5-second timer and
 * are unaffected.
 */
function patchSendBeacon(): void {
	const original = Navigator.prototype.sendBeacon;
	Navigator.prototype.sendBeacon = function (url: string | URL, data?: BodyInit | null): boolean {
		if (typeof url === 'string' && url.includes('dataplane.rum.')) {
			return true;
		}
		return original.call(this, url, data);
	};
}

export function getRumConsent(): RumConsent {
	try {
		const value = localStorage.getItem(RUM_CONSENT_KEY);
		if (value === 'accepted' || value === 'rejected') {
			return value;
		}
	} catch (_error) {
		// Ignore storage errors and fall back to cookieless mode.
	}
	return 'unset';
}

export function setRumConsent(consent: Exclude<RumConsent, 'unset'>): boolean {
	try {
		localStorage.setItem(RUM_CONSENT_KEY, consent);
		return true;
	} catch (_error) {
		// Ignore storage errors; app behavior remains functional.
		return false;
	}
}

export function shouldShowRumConsentBanner(): boolean {
	// Only show banner when RUM is actually configured; no consent needed otherwise.
	if (!process.env.REACT_APP_RUM_APP_MONITOR_ID) {
		return false;
	}
	return getRumConsent() === 'unset';
}

export function initRum(options?: { allowCookies?: boolean }): AwsRum | undefined {
	const appMonitorId = process.env.REACT_APP_RUM_APP_MONITOR_ID;
	const identityPoolId = awsExports.aws_cognito_identity_pool_id as string | undefined;
	const region = awsExports.aws_project_region as string | undefined;

	if (!appMonitorId || !identityPoolId || !region) {
		return undefined;
	}

	try {
		patchSendBeacon();

		const consent = getRumConsent();
		const allowCookies = options?.allowCookies ?? consent === 'accepted';

		const config: AwsRumConfig = {
			sessionSampleRate: 1,
			identityPoolId,
			endpoint: `https://dataplane.rum.${region}.amazonaws.com`,
			telemetries: ['errors', 'performance', 'http'],
			allowCookies,
			enableXRay: false,
			sessionEventLimit: 0,
			compressionStrategy: { enabled: false },
			debug: process.env.NODE_ENV !== 'production'
		};

		awsRum = new AwsRum(appMonitorId, '1.0.0', region, config);
		console.info('[RUM] Initialized successfully', { appMonitorId, region, allowCookies, consent });
	} catch (error) {
		// RUM init failure should never break the app
		console.warn('[RUM] Initialization failed:', error);
	}

	return awsRum;
}

export function getRum(): AwsRum | undefined {
	return awsRum;
}
